<div class="sg-table-container" [class.sg-table-loading]="loading">
  @if (showTable) {
    <div class="sg-table-wrapper">
      @if (loading && !showSkeleton) {
        <div class="sg-table-loading-overlay">
          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
        </div>
      }
      <div class="sg-table-scroller">
        <table
          mat-table
          [dataSource]="dataSource"
          class="sg-table"
          [class.sg-table-fixed]="config.tableLayout === 'fixed'"
          [class.sg-table-header-compact]="config.headerVariant === 'compact'"
          [class.sg-table-resizing]="resizing"
          [class.sg-table-cards]="config.rowStyle === 'cards'"
          [class.sg-table-row-spacing]="config.rowSpacing"
          [class.sg-table-row-border]="config.rowBorderStyle?.enabled"
          [ngStyle]="getTableStyles()"
          matSort
          multiTemplateDataRows>
          <!-- ── Selection column ─────────────────────────────────────────────── -->
          <ng-container matColumnDef="__select__" sticky>
            <th mat-header-cell *matHeaderCellDef class="sg-table-select-header">
              @if (config.multiSelect !== false) {
                <mat-checkbox
                  [checked]="dataSource.data.length > 0 && isAllSelected()"
                  [indeterminate]="dataSource.data.length > 0 && selection.hasValue() && !isAllSelected()"
                  [disabled]="dataSource.data.length === 0"
                  (change)="masterToggle()"></mat-checkbox>
              }
            </th>
            <td mat-cell *matCellDef="let row" class="sg-table-select-cell" (click)="selectRow(row, $event)">
              <mat-checkbox [checked]="selection.isSelected(row)"></mat-checkbox>
            </td>
          </ng-container>

          <!-- ── Expand toggle column ─────────────────────────────────────────── -->
          <ng-container matColumnDef="__expand__">
            <th mat-header-cell *matHeaderCellDef class="sg-table-expand-header"></th>
            <td mat-cell *matCellDef="let row" class="sg-table-expand-cell">
              <button
                mat-icon-button
                class="sg-table-expand-btn"
                [class.sg-table-expand-btn-open]="isExpanded(row)"
                (click)="toggleExpand(row, $event)"
                type="button">
                <mat-icon>{{ isExpanded(row) ? "expand_less" : "expand_more" }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- ── Data columns ─────────────────────────────────────────────────── -->
          @for (column of config.columns; track column) {
            <ng-container [matColumnDef]="transformToString(column.key)" [sticky]="column.sticky === 'start'" [stickyEnd]="column.sticky === 'end'">
              <th
                mat-header-cell
                *matHeaderCellDef
                [mat-sort-header]="column.sortable ? transformToString(column.key) : ''"
                [disabled]="!column.sortable"
                [ngStyle]="{ width: getComputedColumnWidth(column), 'text-align': column.headerAlign || 'left' }"
                [class]="'sg-table-header-' + (column.cellType || 'text')"
                [class.mat-sort-header-sortable]="column.sortable"
                [class.sg-table-header-resizable]="column.resizable"
                [class.sg-table-header-align-left]="column.headerAlign === 'left' || !column.headerAlign"
                [class.sg-table-header-align-center]="column.headerAlign === 'center'"
                [class.sg-table-header-align-right]="column.headerAlign === 'right'">
                <div class="sg-table-header-content" [sgTooltip]="column.headerTooltip">
                  @if (column.headerIcon) {
                    <mat-icon class="sg-table-header-icon">{{ column.headerIcon }}</mat-icon>
                  }
                  <sg-text [variant]="config.headerVariant === 'compact' ? 'sg-body-small-bold' : 'sg-body-medium-regular'">{{ column.header }}</sg-text>
                </div>
                @if (column.resizable) {
                  <div
                    class="sg-table-resize-handle"
                    (mousedown)="onResizeStart($event, column)"
                    (click)="$event.stopPropagation()"></div>
                }
              </th>
              <td
                mat-cell
                *matCellDef="let row"
                [ngStyle]="{ width: getComputedColumnWidth(column), 'text-align': column.cellAlign || 'left' }"
                [class]="getColumnClass(column)">
                <!-- Skeleton placeholder while loading (skeletonRowCount > 0) -->
                @if (showSkeleton) {
                  <span class="sg-table-skeleton-bar" [sgSkeleton]="true" skeletonShape="rectangle" skeletonHeight="14px"></span>
                } @else if (column.editable && column.editTemplate) {
                  <ng-container
                    *ngTemplateOutlet="column.editTemplate; context: { $implicit: row, value: getCellValue(row, column), column: column.key }"></ng-container>
                } @else {
                  <!-- Read-only cell -->
                  @if (column.cellType === "text" || !column.cellType) {
                    @if (!column.tooltip && !column.ellipsis) {
                      <span>{{ formatCellValue(getCellValue(row, column), column) }}</span>
                    }
                    @if (column.tooltip || column.ellipsis) {
                      <span
                        [sgTooltip]="getCellValue(row, column)"
                        [tooltipDescription]="column.tooltipProps?.description || undefined"
                        [tooltipTemplateRef]="column.tooltipProps?.templateRef || undefined"
                        [tooltipPosition]="column.tooltipProps?.position || 'below'"
                        [tooltipArrow]="column.tooltipProps?.arrow || 'auto'"
                        [tooltipShowDelay]="column.tooltipProps?.showDelay || 100"
                        [tooltipHideDelay]="column.tooltipProps?.hideDelay || 100"
                        [tooltipAnimationDuration]="column.tooltipProps?.animationDuration || 200"
                        [showOnOverflow]="column.tooltipProps?.showOnOverflow ?? column.ellipsis ?? false"
                        class="sg-table-cell-with-tooltip">
                        {{ formatCellValue(getCellValue(row, column), column) }}
                      </span>
                    }
                  }
                  @if (column.cellType === "status-tag") {
                    <sg-status-tag
                      [status]="row[column.statusTagProps?.status || column.key]"
                      [text]="formatCellValue(getCellValue(row, column), column)"
                      [size]="column.statusTagProps?.size || 'default'"
                      [withDot]="column.statusTagProps?.withDot ?? true"
                      [withBorder]="column.statusTagProps?.withBorder || false"></sg-status-tag>
                  }
                  @if (column.cellType === "label") {
                    <sg-label
                      [text]="formatCellValue(getCellValue(row, column), column)"
                      [icon]="column.labelProps?.icon || ''"
                      [inverted]="column.labelProps?.inverted || false"
                      [variant]="column.labelProps?.variant || 'neutral'"
                      [size]="column.labelProps?.size || 'medium'"
                      [wrapText]="column.labelProps?.wrapText || false"></sg-label>
                  }
                  @if (column.cellType === "date" || column.cellType === "number") {
                    {{ formatCellValue(getCellValue(row, column), column) }}
                  }
                  @if (column.cellType === "custom") {
                    @if (column.cellRenderer) {
                      <!-- Per-column template (defined in column.cellRenderer TemplateRef) -->
                      <ng-container
                        *ngTemplateOutlet="
                          column.cellRenderer;
                          context: { $implicit: row, column: column, colKey: transformToString(column.key), value: getCellValue(row, column) }
                        "></ng-container>
                    } @else if (cellTemplate) {
                      <!-- Shared #cell fallback — use colKey to switch on column identity -->
                      <ng-container
                        *ngTemplateOutlet="
                          cellTemplate;
                          context: { $implicit: row, col: column, colKey: transformToString(column.key), value: getCellValue(row, column) }
                        "></ng-container>
                    }
                  }
                }
              </td>
            </ng-container>
          }

          <!-- ── Row actions column ────────────────────────────────────────────── -->
          <ng-container matColumnDef="__actions__" stickyEnd>
            <th mat-header-cell *matHeaderCellDef class="sg-table-actions-header"></th>
            <td mat-cell *matCellDef="let row" class="sg-table-actions-cell">
              <div class="sg-table-row-actions" [class.sg-table-row-actions-visible]="config.rowHover">
                <ng-container *ngTemplateOutlet="rowActionsTemplate || null; context: { $implicit: row }"></ng-container>
              </div>
            </td>
          </ng-container>

          <!-- ── Expanded detail column (spans all columns) ────────────────────── -->
          <ng-container matColumnDef="__expandedDetail__">
            <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length" class="sg-table-detail-cell">
              <div class="sg-table-detail-content" [@detailExpand]="isExpanded(row) ? 'expanded' : 'collapsed'">
                @if (expandedRowTemplate) {
                  <ng-container *ngTemplateOutlet="expandedRowTemplate; context: { $implicit: row, row: row }"></ng-container>
                }
              </div>
            </td>
          </ng-container>

          <!-- ── Row definitions ──────────────────────────────────────────────── -->
          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            [class.sg-table-row-clickable]="config.rowClick"
            [class.sg-table-row-hoverable]="config.rowHover"
            [class.sg-table-row-expanded]="config.expandable && isExpanded(row)"
            [ngClass]="getRowClass(row)"
            (click)="onRowClick(row)"></tr>
          <!-- Detail row: always present in DOM when expandable is on; animation handles show/hide -->
          <tr mat-row *matRowDef="let row; columns: ['__expandedDetail__']; when: isDetailRow" class="sg-table-detail-row"></tr>

          <!-- ── No-data row: rendered by MatTable when dataSource is empty ──────── -->
          <tr class="sg-table-no-data-row" *matNoDataRow>
            <td class="sg-table-no-data-cell" [attr.colspan]="displayedColumns.length">
              @if (!loading) {
                <div class="sg-table-no-data-wrapper">
                  <sg-empty-state
                    [variant]="emptyStateConfig.variant || 'empty'"
                    [title]="emptyStateConfig.title"
                    [message]="emptyStateConfig.message"
                    [size]="emptyStateConfig.size || 'small'"></sg-empty-state>
                </div>
              }
            </td>
          </tr>
        </table>
      </div>
    </div>
    @if (config.showPagination) {
      <mat-paginator
        [length]="config.serverSide ? totalCount : dataSource.data.length"
        [pageSize]="config.pageSize || 10"
        [pageSizeOptions]="config.pageSizeOptions || [5, 10, 25, 50]"
        [pageIndex]="currentPage"
        (page)="onPageChange($event)"
        class="sg-table-paginator"></mat-paginator>
    }
  }
</div>
